import { PageViewEventLogger, useUserEventLogger } from '@repo/tracking-service';
import { useAuthContext } from '@repo/user-session';
import { TopNavigation } from '@zenterprise-inc/ui';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { shareUserPromotionQuery } from '@/__generated__/shareUserPromotionQuery.graphql';
import { InviteEventActionButtons } from '@/components/event/share/InviteEventActionButtons';
import { InviteEventBenefit } from '@/components/event/share/InviteEventBenefit';
import { InviteEventGuideline } from '@/components/event/share/InviteEventGuideline';
import { InviteEventSummary } from '@/components/event/share/InviteEventSummary';
import { InviteOverview } from '@/components/event/share/InviteOverview';
import { INVITE_EVENT_PROMOTION_CODE } from '@/lib/constants/event';
import { emptyStaticProps } from '@/lib/web/common-ssr';

const ShareUserPromotionQuery = graphql`
  query shareUserPromotionQuery($promotionCode: String!, $skip: Boolean!) {
    getRefundUserPromotion(promotionCode: $promotionCode) @skip(if: $skip) {
      code
      isTerm
    }
  }
`;

const Share = () => {
  const router = useRouter();
  const { auth } = useAuthContext();
  const { sendClickEvent } = useUserEventLogger();
  const userPromotion = useLazyLoadQuery<shareUserPromotionQuery>(ShareUserPromotionQuery, {
    promotionCode: INVITE_EVENT_PROMOTION_CODE,
    skip: auth.status !== 'login'
  });
  const { status } = auth;

  return (
    <PageViewEventLogger pageName={'invite-promo'}>
      <div className={'sticky top-0 z-[1] w-full bg-white'}>
        <TopNavigation.Basic
          className={'mx-auto max-w-[1024px]'}
          onLeftClick={() => {
            sendClickEvent('invite-promo_body_back');
            router.back();
          }}
          leftIconColor={'gray.800'}
          leftIcon={'back'}
        />
      </div>
      <main className={'flex w-full flex-col items-center'}>
        {status === 'login' && <InviteOverview />}
        <InviteEventSummary />
        <InviteEventBenefit />
        <InviteEventGuideline />
        <InviteEventActionButtons inviteCode={userPromotion.getRefundUserPromotion?.code} privacyAgreement={userPromotion.getRefundUserPromotion?.isTerm} />
      </main>
    </PageViewEventLogger>
  );
};

Share.getLayout = (page: React.ReactNode) => (
  <>
    <Head>
      <title>비즈넵 환급 - 친구 초대하기</title>
      <meta name="title" content="비즈넵 환급 | 수수료 할인받는 친구초대 이벤트 | 사업자라면 바로 참여 가능" />
      <meta name="description" content="친구에게 환급금 조회를 추천해주세요. 사업자는 누구나 참여 가능!" />
    </Head>
    <Script
      src={'https://t1.kakaocdn.net/kakao_js_sdk/2.7.2/kakao.min.js'}
      integrity="sha384-TiCUE00h649CAMonG018J2ujOgDKW/kVWlChEuu4jK2vxfAAD0eZxzCKakxg55G4"
      crossOrigin="anonymous"
      onLoad={() => {
        window.Kakao?.init(process.env.NEXT_PUBLIC_KAKAO_JAVASCRIPT_KEY);
      }}
    />
    {page}
  </>
);

export const getStaticProps = emptyStaticProps;

export default Share;
